import { HomeOutlined, SolutionOutlined, ToolOutlined, UserOutlined } from '@ant-design/icons'
import { Col, Form, FormProps, Row, Select, SelectProps } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import { debounce } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { queryCache } from '../../apollo/hooks'
import { COPROPRIETE_SEARCH } from '../../apollo/query/copropriete.query'
import { PERSONNE_SEARCH } from '../../apollo/query/personne.query'
import { FOURNISSEUR_SEARCH, SOCIETE_SEARCH } from '../../apollo/query/societe.query'
import { COLOR } from '../../constants'
import { useImperativeQuery } from '../../helper'
import { D4_Copropriete, D4_Lot, D4_Societe } from '../../types'
import D4SelectBatiment from '../D4SelectBatiment'
import D4SelectEquipement from '../D4SelectEquipement'
import D4SelectSymptome from '../D4SelectSymptome'
import GestionnaireFormInput from '../GestionnaireFormInput/GestionnaireFormInput'

const useSearch = () => {
  const lot = useImperativeQuery<
    {
      lots: D4_Lot[]
    },
    { search: string }
  >(PERSONNE_SEARCH)
  const societe = useImperativeQuery<
    {
      societes: D4_Lot[]
    },
    { search: string }
  >(SOCIETE_SEARCH)
  const fournisseur = useImperativeQuery<
    {
      fournisseurs: D4_Societe[]
    },
    { search: string }
  >(FOURNISSEUR_SEARCH)
  const copropriete = useImperativeQuery<
    {
      coproprietes: D4_Copropriete[]
    },
    { search: string }
  >(COPROPRIETE_SEARCH)

  const handlers = useMemo(
    () => ({
      copropriete,
      fournisseur,
      lot,
      societe,
    }),
    [copropriete, fournisseur, lot, societe]
  )

  return handlers
}

const contextOptionsFactory = ({
  societes,
  personnes,
  fournisseurs,
  coproprietes,
}: {
  societes?: D4_Lot[]
  personnes?: D4_Lot[]
  fournisseurs?: D4_Societe[]
  coproprietes?: D4_Copropriete[]
}) => {
  /**
   * Uniquement pour les appels entrants
   */
  const fournisseurOptions =
    fournisseurs?.map((societe) => ({
      label: (
        <span role="img">
          <ToolOutlined style={{ marginRight: '5px', color: COLOR.red }} /> {societe.raisonsociale}
        </span>
      ),
      // value: JSON.stringify(societe), // id societe
      value: 'fournisseur-' + societe.id, // id societe
      'data-filter-value': societe.raisonsociale, // la valeur qui va etre utilisé lors du search dans le select
    })) ?? []

  const societeOptions =
    societes?.map((lot) => ({
      label: (
        <span role="img">
          <SolutionOutlined style={{ marginRight: '5px', color: COLOR.darkestGrey }} />
          {lot.copropriete?.trigramme} - {lot.lot_coproprietaire?.coproprietaire.societe?.raisonsociale}
        </span>
      ),
      // value: 'societe-' + lot.copropriete_id + '-' + lot.lot_coproprietaire?.coproprietaire.societe?.id, // id lot
      value: 'societe-' + lot.id, // id lot
      'data-filter-value': lot.lot_coproprietaire?.coproprietaire.societe?.raisonsociale,
      'data-serialized': JSON.stringify(lot),
    })) ?? []

  const personneOptions =
    personnes?.map((lot) => ({
      label: (
        <span role="img">
          <UserOutlined style={{ marginRight: '5px', color: COLOR.blue }} /> {lot.copropriete?.trigramme} -{' '}
          {lot.lot_coproprietaire?.coproprietaire?.personne?.nom} -{' '}
          {lot.lot_coproprietaire?.coproprietaire?.personne?.prenom} -{' '}
          {lot.nature}
        </span>
      ),
      // value: 'personne-' + lot.copropriete_id + '-' + lot.lot_coproprietaire?.coproprietaire?.personne?.id, // id lot
      value: 'personne-' + lot.id, // id lot
      'data-filter-value': `${lot.lot_coproprietaire?.coproprietaire?.personne?.nom} ${lot.lot_coproprietaire?.coproprietaire?.personne?.prenom}`,
      'data-serialized': JSON.stringify(lot),
    })) ?? []

  const coproprieteOptions =
    coproprietes?.map((copro) => ({
      label: (
        <span>
          <HomeOutlined style={{ marginRight: '5px', color: COLOR.green }} /> {copro.trigramme} - {copro.nom}
        </span>
      ),
      value: 'copropriete-' + copro.id, // id copro
      'data-filter-value': copro.trigramme,
      'data-serialized': JSON.stringify(copro),
    })) ?? []

  return [
    { label: 'Fournisseurs', options: fournisseurOptions },
    { label: 'Coproprietés', options: coproprieteOptions },
    { label: 'SCI', options: societeOptions },
    { label: 'Personnes', options: personneOptions },
  ]
}
// const createValueFromContext = (context: ContextDefaultValue | undefined) =>
//   context ? `${context.type}-${context.coproprieteId}-${context.contextId}` : null

type SelectMultiSourceProps = SelectProps<any> & {
  dataSourcesName: string[]
  onSelectContext?: (obj: any) => void
  contextDefaultValue?: ContextDefaultValue
}

const getEntity = (type: string, id: string) => {
  let obj = null
  if (type === 'personne') {
    obj = queryCache({
      id,
      typename: 'd4_lot',
      fields: [`copropriete_id copropriete { id }   lot_coproprietaire {coproprietaire {personne {id}}}`],
    })
  } else if (type === 'copropriete') {
    obj = queryCache({
      id,
      typename: 'd4_copropriete',
      fields: [`id`],
    })
  } else if (type === 'societe') {
    obj = queryCache({
      id,
      typename: 'd4_lot',
      fields: [`copropriete_id copropriete { id }  lot_coproprietaire {coproprietaire {societe {id representant_id}}}`],
    })
  } else if (type === 'fournisseur') {
    obj = queryCache({
      id,
      typename: 'd4_societe',
      fields: [`id representant_id`],
    })
  }
  return obj
}

const SelectMultiSources: React.FC<SelectMultiSourceProps> = ({
  value,
  onChange,
  dataSourcesName,
  onSelectContext,
  contextDefaultValue,
}) => {
  const search = useSearch() as any
  const [contextOptions, setContextOptions] = useState<any>()

  const defaultValueFetch = useCallback(
    async (type: string, id: number) => {
      if (type === 'personne') {
        const personnes = await search.lot({ search: '', id })

        setContextOptions(
          contextOptionsFactory({
            personnes: personnes?.data?.lots,
            // societes: societes?.data?.societes,
            // fournisseurs: fournisseurs?.data?.fournisseurs,
            // coproprietes: coproprietes?.data?.coproprietes,
          })
        )
      }
      // else if (type === 'societe') {
      //   const societes = await search.societe({ search: '', id })
      //   setContextOptions(
      //     contextOptionsFactory({
      //       // personnes: personnes?.data?.lots,
      //       societes: societes?.data?.societes,
      //       // fournisseurs: fournisseurs?.data?.fournisseurs,
      //       // coproprietes: coproprietes?.data?.coproprietes,
      //     })
      //   )
      // }
    },
    [search]
  )

  useEffect(() => {
    if (value && contextDefaultValue?.id && Number(value.split('-')[1]) === contextDefaultValue?.id) {
      const { type, id } = contextDefaultValue
      defaultValueFetch(type, id).then(() =>
        onSelectContext?.({
          type,
          data: getEntity(type, id.toString()),
        })
      )
    }
  }, [contextDefaultValue, contextDefaultValue?.id, defaultValueFetch, onSelectContext, search, value])

  const handleSearch = debounce(async (text: string) => {
    if (!text) return
    if (text.length >= 3) {
      /**
       * types societe, copropriete ...
       */

      const res = await Promise.all(
        dataSourcesName.map((source) => {
          if (source === 'personne') {
            return search.lot({ search: `%${text}%`, id: -1 })
          }
          if (source === 'societe') {
            return search.societe({ search: `%${text}%` })
          }
          if (source === 'copropriete') {
            return search.copropriete({ search: `%${text}%` })
          }
          if (source === 'fournisseur') {
            return search.fournisseur({ search: `%${text}%` })
          }
          return null
        })
      )
      const options = res.reduce((acc, cur) => {
        acc.personnes = acc.personnes ?? cur.data.lots
        acc.coproprietes = acc.coproprietes ?? cur.data.coproprietes
        acc.societes = acc.societes ?? cur.data.societes
        acc.fournisseurs = acc.fournisseurs ?? cur.data.fournisseurs
        return acc
      }, {})

      setContextOptions(contextOptionsFactory(options))
    }
  }, 300)

  const handleOnChange = (value: string, options: any) => {
    const [type, id] = value.split('-')
    onSelectContext?.({
      type,
      data: getEntity(type, id),
    })
    onChange?.(value, options)
  }

  return (
    <Select
      onSearch={handleSearch}
      placeholder="Recherche"
      optionFilterProp="data-filter-value"
      showSearch
      options={contextOptions}
      onChange={handleOnChange}
      value={value}
    />
  )
}

const MultiSelect: React.FC<
  FormProps & {
    onSelectContext?: (obj: any) => void
    withGestionnaires?: boolean
    contextDefaultValue?: ContextDefaultValue
  }
> = ({ onFieldsChange, onValuesChange, onSelectContext, withGestionnaires = false, contextDefaultValue, ...rest }) => {
  const [form] = useForm()

  const updateContext = useCallback(
    (context: any) => {
      const [type, id] = context.split('-')
      const data = getEntity(type, id)

      if (type === 'personne') {
        form.setFieldsValue({
          coproprieteId: (data as D4_Lot).copropriete_id,
          contextId: (data as D4_Lot).lot_coproprietaire?.coproprietaire.personne?.id,
          type,
        })
      } else if (type === 'societe') {
        form.setFieldsValue({
          coproprieteId: (data as D4_Lot).copropriete_id,
          contextId: (data as D4_Lot).lot_coproprietaire?.coproprietaire.societe?.id,
          type,
        })
      } else if (type === 'copropriete') {
        form.setFieldsValue({
          coproprieteId: (data as D4_Copropriete)?.id,
          contextId: (data as D4_Copropriete)?.id,
          type,
        })
      }
    },
    [form]
  )
  const onValuesChanges = useCallback(
    (value: any) => {
      if (value.context) {
        updateContext(value.context)
      }
    },
    [updateContext]
  )

  useEffect(() => {
    if (!contextDefaultValue) return

    if (contextDefaultValue.id && !form.getFieldValue('context')) {
      form.setFieldsValue({ context: `${contextDefaultValue.type}-${contextDefaultValue.id}` })
    }
    if (contextDefaultValue.batimentId && !form.getFieldValue('batiment')) {
      form.setFieldsValue({ batiment: contextDefaultValue.batimentId })
    }
    if (contextDefaultValue.equipementId && !form.getFieldValue('equipement')) {
      form.setFieldsValue({ equipement: contextDefaultValue.equipementId })
    }
  }, [contextDefaultValue, contextDefaultValue?.id, form])

  return (
    <Form {...rest} form={form} name="selectMultiSources" onValuesChange={onValuesChanges}>
      <Row justify="center" gutter={[24, 12]}>
        <Col flex="1 1 300px">
          {/* hidden fields pour la gestion du multi select */}
          <Form.Item hidden name="coproprieteId" />
          <Form.Item hidden name="contextId" />
          <Form.Item hidden name="type" />
          <Form.Item
            name="context"
            rules={[{ required: true }]}
            shouldUpdate={(prev, cur) => {
              if (prev.context !== cur.context) {
                updateContext(cur.context)
                return true
              }
              return false
            }}
          >
            <SelectMultiSources
              dataSourcesName={['societe', 'personne', 'copropriete']}
              onSelectContext={onSelectContext}
              contextDefaultValue={contextDefaultValue}
            />
          </Form.Item>
        </Col>
        <Col flex="1 1 300px">
          <Form.Item
            shouldUpdate={(prev, curr) => {
              if (prev.contextId !== curr.contextId) {
                form.setFieldsValue({
                  batiment: contextDefaultValue?.batimentId ?? form.getFieldValue('coproprieteId'),
                })
                return true
              }
              return false
            }}
          >
            {() => (
              <Form.Item name="batiment" rules={[{ required: true }]}>
                <D4SelectBatiment
                  coproprieteId={form.getFieldValue('coproprieteId')}
                  disabled={!form.getFieldValue('coproprieteId')}
                />
              </Form.Item>
            )}
          </Form.Item>
        </Col>
        <Col flex="1 1 300px">
          <Form.Item shouldUpdate={(prev, curr) => prev.batiment !== curr.batiment}>
            {() => (
              <Form.Item name="equipement" rules={[{ required: true }]}>
                <D4SelectEquipement
                  coproprieteId={form.getFieldValue('coproprieteId')}
                  disabled={!form.getFieldValue('batiment')}
                />
              </Form.Item>
            )}
          </Form.Item>
        </Col>
        <Col flex="1 1 300px">
          <Form.Item
            shouldUpdate={(prev, curr) => {
              if (prev.equipement !== curr.equipement) {
                form.resetFields(['symptome'])
                return true
              }
              return false
            }}
          >
            {() => (
              <Form.Item name="symptome">
                <D4SelectSymptome
                  equipementId={form.getFieldValue('equipement')}
                  placeholder="Symptome"
                  disabled={!form.getFieldValue('equipement')}
                />
              </Form.Item>
            )}
          </Form.Item>
        </Col>
        {withGestionnaires && (
          <Col flex="1 1 300px">
            <Form.Item name="gestionnaire" rules={[{ required: true }]}>
              <GestionnaireFormInput />
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  )
}

export default MultiSelect
